import axios, { type AxiosError, type AxiosResponse } from 'axios'
import { signOut } from 'next-auth/react'
import type { BaseParams, PageableConfig, PageableResponse } from './types'
import { getToken } from 'lib/services/auth'
import { dispatchToaster } from 'lib/utils/notifications'
import { logger } from 'lib/analytics/logger'

export const axiosInstance = axios.create({
  baseURL: `${process.env.NEXT_PUBLIC_HOST}/api/v2`,
  headers: {
    'X-Requested-With': 'XMLHttpRequest'
  },
  withCredentials: true
})

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      return await signOut({ redirect: true, callbackUrl: '/auth/login' })
    }

    if (error.response?.status === 422) {
      const { message } = error.response
        .data as LaravelValidationResponse['data']

      dispatchToaster({
        title: 'Oops!',
        message,
        variant: 'error'
      })

      logger.error('Error 422 from request', {}, error)
    }

    throw error
  }
)

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken()

    if (token) {
      // eslint-disable-next-line
      // @ts-ignore
      config.headers = {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json'
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

export const apiV2 = {
  get: async <Data, Params extends BaseParams = BaseParams>(
    url: string,
    { params }: { params?: Params } = {}
  ) =>
    axiosInstance.request<Data>({
      method: 'GET',
      url,
      params
    }),

  post: async <
    ResponseData,
    RequestData = unknown,
    Params extends BaseParams = BaseParams
  >(
    url: string,
    {
      params,
      data,
      headers
    }: {
      params?: Params
      data?: RequestData | File
      headers?: object
    } = {}
  ) => {
    return axiosInstance.request<ResponseData>({
      method: 'POST',
      url,
      params,
      data,
      headers
    })
  },
  patch: async <ResponseData, RequestData = unknown>(
    url: string,
    { data }: { data?: RequestData } = {}
  ) =>
    axiosInstance.request<ResponseData>({
      method: 'PATCH',
      url,
      data
    }),
  put: async <ResponseData, RequestData = unknown>(
    url: string,
    { data }: { data?: RequestData } = {}
  ) =>
    axiosInstance.request<ResponseData>({
      method: 'PUT',
      url,
      data
    }),
  delete: async <ResponseData, RequestData = unknown>(
    url: string,
    { data }: { data?: RequestData } = {}
  ) =>
    axiosInstance.request<ResponseData>({
      method: 'DELETE',
      url,
      data
    }),
  getPaginated: async <Data, Params extends BaseParams = BaseParams>(
    url: string,
    params?: PageableConfig<Params>
  ) => {
    return axiosInstance.request<PageableResponse<Data>>({
      method: 'GET',
      url,
      params
    })
  }
}

interface LaravelValidationResponse extends AxiosResponse {
  status: number
  data: {
    message: string
    errors: Record<string, Array<string>>
  }
}
